import React, { MouseEvent, useCallback, useMemo } from 'react';

import { Icon } from '@rbilabs/component-library';
import { differenceInMinutes } from 'date-fns';
import { useIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router-dom';

import { DeliveryStatus, RbiOrderStatus, useGetUserOrdersQuery } from 'generated/rbi-graphql';
import {
  STATUS_MESSAGES,
  STATUS_PERCENTS
} from 'pages/order-confirmation/delivery/delivery-progress';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { BottomServiceVariants, IBottomServiceModeDetail } from '../types';

const DELIVERY_COMPLETED = [
  DeliveryStatus.ORDER_DROPPED_OFF,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_CANCELLED
];

const ORDER_PROGRESS_POLLING_DURATION_IN_MS = 10000;
const DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES = 10;
const RECENT_ORDER_UPDATED_TIMEOUT_IN_MINUTES = 60;

export const getDeliveryHeading = ({ deliveryStatus }: { deliveryStatus: DeliveryStatus }) => {
  const deliveryProgress = STATUS_PERCENTS[deliveryStatus];
  if (deliveryProgress <= STATUS_PERCENTS[DeliveryStatus.ORDER_CREATED]) {
    return 'orderPlaced';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_PICKED_UP]) {
    return 'deliveryOrderBeingPrepared';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_DROPPED_OFF]) {
    return 'deliveryDriverEnroute';
  }
  return 'deliveryDelivered';
};

export const useBottomServiceModeDetails = (): IBottomServiceModeDetail => {
  const {
    serviceMode,
    deliveryAddress,
    calculateCartTotal,
    canUserCheckout,
    curbsidePickupOrderId
  } = useOrderContext();
  const { store } = useStoreContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();
  const { logCheckoutEvent, logEvent } = useMParticleContext();
  const isOnMenuPage = useMatch({
    path: routes.menu,
    end: false
  });
  const navigate = useNavigate();
  const { data, startPolling, stopPolling } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [RbiOrderStatus.INSERT_SUCCESSFUL, RbiOrderStatus.UPDATE_SUCCESSFUL]
    }
  });
  const cartComponent = useMemo(() => formatCurrencyForLocale(calculateCartTotal()), [
    calculateCartTotal,
    formatCurrencyForLocale
  ]);
  const handleCartClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      logCheckoutEvent(serviceMode);
      navigate(routes.cart);
    },
    [logCheckoutEvent, navigate, serviceMode]
  );
  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode
    });
    navigate(routes.serviceMode);
  }, [logEvent, navigate, serviceMode]);
  const handleNavigateToOrderConfirmation = useCallback(
    (rbiOrderId: string) => () => {
      logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
        'Service Mode': serviceMode,
        'Order Id': rbiOrderId
      });
      navigate(`${routes.orderConfirmation}/${rbiOrderId}`);
    },
    [logEvent, navigate, serviceMode]
  );
  // We don't want to block user from ordering when there is existing
  // order in progress. We will always shows default bottom service mode
  // on menu page
  const recentOrder = data?.userOrders?.orders?.[0];
  // Completed delivery order is when status is completed and
  // exceeded 10 minutes after order is dropped off (in order for user to view the order)
  const isDeliveryOrderCompleted =
    recentOrder?.delivery &&
    DELIVERY_COMPLETED.includes(recentOrder.delivery.status) &&
    Math.abs(differenceInMinutes(new Date(recentOrder.updatedAt), new Date())) >
      DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES;
  if (!isOnMenuPage && recentOrder?.delivery && !isDeliveryOrderCompleted) {
    if (!DELIVERY_COMPLETED.includes(recentOrder.delivery.status)) {
      startPolling(ORDER_PROGRESS_POLLING_DURATION_IN_MS);
    }
    const deliveryStatus = recentOrder.delivery.status;
    const heading = formatMessage({
      id: getDeliveryHeading({ deliveryStatus })
    });
    const details = formatMessage({ id: STATUS_MESSAGES[deliveryStatus] });
    const buttonContentText =
      deliveryStatus === DeliveryStatus.ORDER_DROPPED_OFF
        ? formatMessage({ id: 'viewDetails' })
        : formatMessage({ id: 'track' });
    return {
      heading,
      details,
      buttonContentText,
      buttonContentIcon: null,
      icon: <Icon icon="markerFilled" color="white" width="1.75rem" height="1.75rem" />,
      onButtonClick: handleNavigateToOrderConfirmation(recentOrder.rbiOrderId),
      onBottomServiceClick: handleNavigateToOrderConfirmation(recentOrder.rbiOrderId),
      variant: BottomServiceVariants.IN_PROGRESS
    };
  }
  if (
    !isOnMenuPage &&
    recentOrder &&
    !recentOrder?.delivery &&
    Math.abs(differenceInMinutes(new Date(recentOrder?.updatedAt), new Date())) <
      RECENT_ORDER_UPDATED_TIMEOUT_IN_MINUTES
  ) {
    return {
      heading: formatMessage({ id: 'orderPlaced' }),
      details: formatMessage({ id: 'yourOrderIsBeingPrepared' }),
      icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
      buttonContentText: formatMessage({ id: 'viewDetails' }),
      buttonContentIcon: null,
      onButtonClick: handleNavigateToOrderConfirmation(recentOrder.rbiOrderId),
      onBottomServiceClick: handleNavigateToOrderConfirmation(recentOrder.rbiOrderId),
      variant: BottomServiceVariants.IN_PROGRESS
    };
  }
  if (!isOnMenuPage && curbsidePickupOrderId) {
    return {
      heading: formatMessage({ id: 'serviceModeDetailsCurbsideHeader' }),
      details: formatMessage({ id: 'headToRestaurant' }),
      icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
      buttonContentText: formatMessage({ id: 'confirmArrival' }),
      buttonContentIcon: null,
      onButtonClick: handleNavigateToOrderConfirmation(curbsidePickupOrderId),
      onBottomServiceClick: handleNavigateToOrderConfirmation(curbsidePickupOrderId),
      variant: BottomServiceVariants.IN_PROGRESS
    };
  }
  stopPolling();
  if (store && '_id' in store && store._id) {
    if ((!store.hasMobileOrdering || !enableOrdering) && store?.physicalAddress?.address1) {
      return {
        heading: formatMessage({ id: 'yourSelectedStore' }),
        details: store.physicalAddress.address1,
        icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
        buttonContentText: '',
        buttonContentIcon: null,
        onButtonClick: handleCartClick,
        onBottomServiceClick: handleServiceModeChange,
        buttonDisabled: !canUserCheckout,
        variant: BottomServiceVariants.DEFAULT
      };
    }
    switch (serviceMode) {
      case ServiceMode.DELIVERY:
        if (deliveryAddress) {
          return {
            heading: formatMessage({ id: 'delivery' }),
            details: formatMessage({ id: 'toAddress' }, { address: deliveryAddress.addressLine1 }),
            icon: <Icon icon="delivery" color="white" width="1.75rem" height="1.75rem" />,
            buttonContentText: cartComponent,
            buttonContentIcon: <Icon icon="bag" color="primary" />,
            onButtonClick: handleCartClick,
            onBottomServiceClick: handleServiceModeChange,
            buttonDisabled: !canUserCheckout,
            variant: BottomServiceVariants.DEFAULT
          };
        }
        break;
      case ServiceMode.TAKEOUT:
      case ServiceMode.CURBSIDE:
      case ServiceMode.DRIVE_THRU:
      case ServiceMode.EAT_IN:
        if (store?.physicalAddress) {
          return {
            heading: formatMessage({ id: 'pickUp' }),
            details: formatMessage(
              { id: 'fromStoreAddress' },
              { storeAddress: store.physicalAddress.address1 }
            ),
            icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
            buttonContentText: cartComponent,
            buttonContentIcon: <Icon icon="bag" color="primary" />,
            onButtonClick: handleCartClick,
            onBottomServiceClick: handleServiceModeChange,
            buttonDisabled: !canUserCheckout,
            variant: BottomServiceVariants.DEFAULT
          };
        }
        break;
      default:
    }
  }
  return {
    heading: formatMessage({ id: 'chooseLocation' }),
    details: formatMessage({ id: 'forItemAvailability' }),
    icon: <Icon icon="markerFilled" color="white" width="1.75rem" height="1.75rem" />,
    buttonContentText: formatMessage({ id: 'locations' }),
    buttonContentIcon: null,
    onButtonClick: handleServiceModeChange,
    onBottomServiceClick: handleServiceModeChange,
    variant: BottomServiceVariants.DEFAULT
  };
};
